/**
 * ReportScheduleWizard - User-friendly wizard for scheduling recurring reports
 *
 * Steps:
 * 1. Select report type
 * 2. Configure frequency
 * 3. Set recipients
 * 4. Preview & confirm
 */

import React, { useState, useEffect } from 'react';
import { getAuthHeaders } from '../../api/core.js';
import { useTranslation } from 'react-i18next';
import { reportAPI } from '../../api';

// `label`/`description` = repli FR — l'affichage passe par t('reports.schedule.frequencies.<value>.*')
const FREQUENCIES = [
  { value: 'daily', label: 'Quotidien', icon: '📅', description: 'Chaque jour ouvré à 8h' },
  { value: 'weekly', label: 'Hebdomadaire', icon: '📆', description: 'Chaque lundi à 8h' },
  { value: 'monthly', label: 'Mensuel', icon: '🗓️', description: 'Le 1er de chaque mois' },
  { value: 'quarterly', label: 'Trimestriel', icon: '📊', description: 'Tous les 3 mois' },
  { value: 'yearly', label: 'Annuel', icon: '📈', description: 'Le 1er janvier' }
];

const FORMATS = [
  { value: 'csv', label: 'CSV', icon: '📊', description: 'Tableur (Excel compatible)' },
  { value: 'pdf', label: 'PDF', icon: '📄', description: 'Document formaté' }
];

export default function ReportScheduleWizard({ isOpen, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [reportTypes, setReportTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Form state - selectedTypes is now a Set for multi-select
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [frequency, setFrequency] = useState('weekly');
  const [formats, setFormats] = useState(['csv']);
  const [recipients, setRecipients] = useState([{ email: '', name: '' }]);
  const [isActive, setIsActive] = useState(true);

  // Toggle report type selection (API returns 'id', not 'code')
  const toggleReportType = (id) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Select/deselect all
  const toggleSelectAll = () => {
    if (selectedTypes.size === reportTypes.length) {
      setSelectedTypes(new Set());
    } else {
      setSelectedTypes(new Set(reportTypes.map(t => t.id)));
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadReportTypes();
    }
  }, [isOpen]);

  const loadReportTypes = async () => {
    try {
      const response = await reportAPI.getTypes();
      if (response.ok !== false && response.data) {
        setReportTypes(response.data);
      }
    } catch (err) {
      console.error('Failed to load report types:', err);
    }
  };

  const handleAddRecipient = () => {
    setRecipients([...recipients, { email: '', name: '' }]);
  };

  const handleRemoveRecipient = (index) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  const handleRecipientChange = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const toggleFormat = (format) => {
    if (formats.includes(format)) {
      if (formats.length > 1) {
        setFormats(formats.filter(f => f !== format));
      }
    } else {
      setFormats([...formats, format]);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);

    try {
      const validRecipients = recipients.filter(r => r.email.trim());
      const typesToCreate = Array.from(selectedTypes);
      const results = [];
      const errors = [];

      // Create a subscription for each selected report type
      for (const reportType of typesToCreate) {
        try {
          const response = await fetch('/api/v1/report-subscriptions', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
            body: JSON.stringify({
              report_type: reportType,
              schedule_type: frequency,
              formats_json: JSON.stringify(formats),
              recipients: validRecipients.map(r => r.email),
              is_active: isActive
            })
          });

          const result = await response.json();
          if (response.ok || result.success) {
            results.push(result);
          } else {
            errors.push(`${reportType}: ${result.error?.message || result.error || t('common.states.error')}`);
          }
        } catch (err) {
          errors.push(`${reportType}: ${err.message}`);
        }
      }

      if (results.length > 0) {
        if (onSuccess) onSuccess({ created: results.length, total: typesToCreate.length });
        if (errors.length > 0) {
          setError(t('reports.schedule.partialSuccess', { created: results.length, total: typesToCreate.length, errors: errors.join('; ') }));
        } else {
          handleClose();
        }
      } else {
        setError(errors.join('; ') || t('reports.schedule.createError'));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedTypes(new Set());
    setFrequency('weekly');
    setFormats(['csv']);
    setRecipients([{ email: '', name: '' }]);
    setError(null);
    onClose();
  };

  const canProceed = () => {
    switch (step) {
      case 1: return selectedTypes.size > 0;
      case 2: return frequency && formats.length > 0;
      case 3: return recipients.some(r => r.email.trim());
      default: return true;
    }
  };

  if (!isOpen) return null;

  // Get info for all selected types (API uses 'id' not 'code')
  const selectedTypesInfo = reportTypes.filter(t => selectedTypes.has(t.id));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-600 to-indigo-600">
          <h2 className="text-xl font-semibold text-white">
            {t('reports.schedule.title')}
          </h2>
          <p className="text-blue-100 text-sm mt-1">
            {t('reports.schedule.subtitle')}
          </p>
        </div>

        {/* Progress Steps */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            {[
              { num: 1, label: t('reports.schedule.steps.type') },
              { num: 2, label: t('reports.schedule.steps.frequency') },
              { num: 3, label: t('reports.schedule.steps.recipients') },
              { num: 4, label: t('reports.schedule.steps.confirm') }
            ].map((s, i) => (
              <React.Fragment key={s.num}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    step >= s.num
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-200 text-slate-500'
                  }`}>
                    {step > s.num ? '✓' : s.num}
                  </div>
                  <span className={`text-sm hidden sm:block ${
                    step >= s.num ? 'text-slate-800' : 'text-slate-400'
                  }`}>
                    {s.label}
                  </span>
                </div>
                {i < 3 && (
                  <div className={`flex-1 h-0.5 mx-2 ${
                    step > s.num ? 'bg-blue-600' : 'bg-slate-200'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content - Fixed height to prevent yo-yo effect between steps */}
        <div className="p-6 h-[400px] overflow-y-auto">
          {/* Step 1: Select Report Type(s) - Multi-select */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-slate-800">{t('reports.schedule.whichReports')}</h3>
                <button
                  type="button"
                  onClick={toggleSelectAll}
                  className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  {selectedTypes.size === reportTypes.length ? (
                    <>✕ {t('reports.schedule.deselectAll')}</>
                  ) : (
                    <>✓ {t('reports.schedule.selectAll')}</>
                  )}
                </button>
              </div>
              <p className="text-sm text-slate-500">{t('reports.schedule.clickToSelect')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {reportTypes.map((type) => {
                  const isSelected = selectedTypes.has(type.id);
                  return (
                    <button
                      key={type.id}
                      onClick={() => toggleReportType(type.id)}
                      className={`p-4 text-left rounded-lg border-2 transition-all relative ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      {/* Selection checkmark */}
                      {isSelected && (
                        <div className="absolute top-2 right-2 w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
                          <span className="text-white text-sm">✓</span>
                        </div>
                      )}
                      <div className="font-medium text-slate-800 pr-8">{t(`reports.catalog.${type.id}.name`, { defaultValue: type.title })}</div>
                      <div className="text-sm text-slate-500 mt-1">{t(`reports.catalog.${type.id}.description`, { defaultValue: type.description })}</div>
                      <div className="mt-2">
                        <span className={`inline-block px-2 py-0.5 text-xs rounded-full ${
                          type.category === 'compliance' ? 'bg-blue-100 text-blue-800' :
                          type.category === 'cost' ? 'bg-green-100 text-green-800' :
                          type.category === 'risk' ? 'bg-amber-100 text-amber-800' :
                          'bg-slate-100 text-slate-800'
                        }`}>
                          {t(`reports.schedule.categories.${type.category}`, { defaultValue: type.category })}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedTypes.size > 0 && (
                <div className="text-sm text-blue-600 font-medium">
                  {t('reports.schedule.selectedCount', { count: selectedTypes.size })}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Frequency & Format */}
          {step === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="font-medium text-slate-800 mb-3">{t('reports.schedule.whichFrequency')}</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {FREQUENCIES.map((freq) => (
                    <button
                      key={freq.value}
                      onClick={() => setFrequency(freq.value)}
                      className={`p-3 text-center rounded-lg border-2 transition-all ${
                        frequency === freq.value
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{freq.icon}</div>
                      <div className="font-medium text-slate-800">{t(`reports.schedule.frequencies.${freq.value}.label`, { defaultValue: freq.label })}</div>
                      <div className="text-xs text-slate-500">{t(`reports.schedule.frequencies.${freq.value}.description`, { defaultValue: freq.description })}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-medium text-slate-800 mb-3">{t('reports.schedule.whichFormats')}</h3>
                <div className="flex gap-3">
                  {FORMATS.map((fmt) => (
                    <button
                      key={fmt.value}
                      onClick={() => toggleFormat(fmt.value)}
                      className={`flex-1 p-3 text-center rounded-lg border-2 transition-all ${
                        formats.includes(fmt.value)
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <div className="text-2xl mb-1">{fmt.icon}</div>
                      <div className="font-medium text-slate-800">{fmt.label}</div>
                      <div className="text-xs text-slate-500">{t(`reports.schedule.formats.${fmt.value}`, { defaultValue: fmt.description })}</div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Recipients */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="font-medium text-slate-800">{t('reports.schedule.whoReceives')}</h3>

              <div className="space-y-3">
                {recipients.map((recipient, index) => (
                  <div key={index} className="flex gap-3 items-start">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      <input
                        type="email"
                        value={recipient.email}
                        onChange={(e) => handleRecipientChange(index, 'email', e.target.value)}
                        placeholder="email@example.com"
                        className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        type="text"
                        value={recipient.name}
                        onChange={(e) => handleRecipientChange(index, 'name', e.target.value)}
                        placeholder={t('reports.schedule.nameOptional')}
                        className="px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    {recipients.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveRecipient(index)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded-md"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={handleAddRecipient}
                className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
              >
                <span>+</span> {t('reports.schedule.addRecipient')}
              </button>

              <div className="pt-4 border-t border-slate-200">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded border-slate-300 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-700">
                    {t('reports.schedule.activateNow')}
                  </span>
                </label>
              </div>
            </div>
          )}

          {/* Step 4: Confirmation */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="font-medium text-slate-800">{t('reports.schedule.summary')}</h3>

              <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-start">
                  <span className="text-slate-500">{t('reports.schedule.reportWord', { count: selectedTypesInfo.length })}</span>
                  <div className="text-right">
                    {selectedTypesInfo.length === 1 ? (
                      <span className="font-medium text-slate-800">{t(`reports.catalog.${selectedTypesInfo[0]?.id}.name`, { defaultValue: selectedTypesInfo[0]?.title })}</span>
                    ) : (
                      <div className="space-y-1">
                        {selectedTypesInfo.map(type => (
                          <div key={type.id} className="font-medium text-slate-800 text-sm">
                            {t(`reports.catalog.${type.id}.name`, { defaultValue: type.title })}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reports.schedule.frequency')}</span>
                  <span className="font-medium text-slate-800">
                    {t(`reports.schedule.frequencies.${frequency}.label`, { defaultValue: FREQUENCIES.find(f => f.value === frequency)?.label })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reports.schedule.formatsLabel')}</span>
                  <span className="font-medium text-slate-800">
                    {formats.map(f => f.toUpperCase()).join(', ')}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reports.schedule.steps.recipients')}</span>
                  <span className="font-medium text-slate-800">
                    {t('reports.schedule.people', { count: recipients.filter(r => r.email.trim()).length })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('reports.schedule.status')}</span>
                  <span className={`font-medium ${isActive ? 'text-green-600' : 'text-slate-500'}`}>
                    {isActive ? t('reports.schedule.active') : t('reports.schedule.inactive')}
                  </span>
                </div>
                {selectedTypesInfo.length > 1 && (
                  <div className="pt-2 border-t border-slate-200 text-sm text-blue-600">
                    {t('reports.schedule.subscriptionsWillBeCreated', { count: selectedTypesInfo.length })}
                  </div>
                )}
              </div>

              {recipients.filter(r => r.email.trim()).length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="text-sm text-blue-800">
                    <strong>{t('reports.schedule.steps.recipients')} :</strong>
                    <ul className="mt-1 space-y-0.5">
                      {recipients.filter(r => r.email.trim()).map((r, i) => (
                        <li key={i}>{r.name ? `${r.name} <${r.email}>` : r.email}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3 text-sm text-red-700">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-between">
          <button
            type="button"
            onClick={() => step > 1 ? setStep(step - 1) : handleClose()}
            className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-md transition-colors"
          >
            {step > 1 ? t('common.actions.previous') : t('common.actions.cancel')}
          </button>

          <button
            type="button"
            onClick={() => step < 4 ? setStep(step + 1) : handleSubmit()}
            disabled={!canProceed() || loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('reports.schedule.creating')}
              </>
            ) : step < 4 ? (
              t('common.actions.next')
            ) : (
              t('reports.schedule.create')
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
